import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormatService }         from '../../format.service';
import { LoanCalculatorService } from '../../loan-calculator.service';
import { ChartService }          from '../../chart.service';
import {
  LoanSummary, YearlyPayment,
  Prepayment, ScheduleResult, LOAN_CONSTRAINTS
} from '../../models';
import { environment } from '../../../environments/environment';

/**
 * Dedicated Home Loan Prepayment / Part-Payment Calculator.
 *
 * Reuses the shared LoanCalculatorService (same reducing-balance math as the
 * full EMI calculator) but presents a prepayment-first UX: a one-time lump-sum
 * and/or a recurring monthly extra, with a side-by-side "without vs with
 * prepayment" comparison of interest, tenure and EMI.
 */
@Component({
  selector: 'app-home-loan-prepayment-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  providers: [ChartService],
  templateUrl: './home-loan-prepayment-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home-loan-prepayment-calculator.scss']
})
export class HomeLoanPrepaymentCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(LoanCalculatorService);
  private charts = inject(ChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('pieChartRef')  pieChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('barChartRef')  barChartRef!:  ElementRef<HTMLCanvasElement>;
  @ViewChild('lineChartRef') lineChartRef!: ElementRef<HTMLCanvasElement>;

  readonly C = LOAN_CONSTRAINTS;
  readonly Math = Math;

  // ── Loan inputs ──────────────────────────────────────────────────────────
  outstandingAmount = 5_000_000;
  annualInterestRate = 8.5;
  remainingTermYears = 20;

  // ── Prepayment inputs ────────────────────────────────────────────────────
  prepaymentOption: 'reduce_tenure' | 'reduce_emi' = 'reduce_tenure';
  lumpSumAmount = 500_000;
  lumpSumMonth = 12;
  monthlyExtra = 0;

  // ── Validation ───────────────────────────────────────────────────────────
  formErrors:    Record<string, string> = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  // ── Results ──────────────────────────────────────────────────────────────
  calculated  = false;
  calculating = false;
  calcError   = false;
  baseSummary:     LoanSummary | null = null;
  finalSummary:    LoanSummary | null = null;
  yearlySchedule:  YearlyPayment[]    = [];
  private baseYearly: YearlyPayment[] = [];
  hasPrepayments = false;

  // ── Derived ───────────────────────────────────────────────────────────────

  private currentMonthStr(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  get maxMonths(): number { return Math.floor(this.remainingTermYears || 0) * 12; }

  get interestSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalInterest - this.finalSummary.totalInterest);
  }

  get monthsSaved(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.totalMonths - this.finalSummary.totalMonths);
  }

  get emiReduced(): number {
    if (!this.baseSummary || !this.finalSummary) return 0;
    return Math.max(0, this.baseSummary.emi - this.finalSummary.emi);
  }

  get totalPrepaid(): number {
    let total = 0;
    if (this.monthlyExtra > 0) total += this.monthlyExtra * (this.finalSummary?.totalMonths ?? this.maxMonths);
    if (this.lumpSumAmount > 0) total += this.lumpSumAmount;
    return total;
  }

  // ── Validation ──────────────────────────────────────────────────────────

  private validate(): boolean {
    const e: Record<string, string> = {};
    const C = this.C;

    if (this.outstandingAmount == null || isNaN(this.outstandingAmount) || this.outstandingAmount <= 0) {
      e['outstandingAmount'] = 'Outstanding amount is required.';
    } else if (this.outstandingAmount < C.amount.min) {
      e['outstandingAmount'] = `Minimum is ${this.fmt.currency(C.amount.min)}.`;
    } else if (this.outstandingAmount > C.amount.max) {
      e['outstandingAmount'] = `Maximum is ${this.fmt.currency(C.amount.max)}.`;
    }

    if (this.annualInterestRate == null || isNaN(this.annualInterestRate) || this.annualInterestRate <= 0) {
      e['annualInterestRate'] = 'Interest rate is required.';
    } else if (this.annualInterestRate > C.rate.max) {
      e['annualInterestRate'] = `Maximum rate is ${C.rate.max}%.`;
    }

    if (this.remainingTermYears == null || isNaN(this.remainingTermYears) || this.remainingTermYears <= 0) {
      e['remainingTermYears'] = 'Remaining tenure is required.';
    } else if (!Number.isInteger(this.remainingTermYears)) {
      e['remainingTermYears'] = 'Tenure must be a whole number of years.';
    } else if (this.remainingTermYears > C.term.max) {
      e['remainingTermYears'] = `Maximum tenure is ${C.term.max} years.`;
    }

    // At least one prepayment must be present and valid
    const hasLump    = this.lumpSumAmount > 0;
    const hasMonthly = this.monthlyExtra > 0;
    if (!hasLump && !hasMonthly) {
      e['prepayment'] = 'Enter a lump-sum and/or a monthly extra payment.';
    }
    if (hasLump && (this.lumpSumMonth < 1 || this.lumpSumMonth > (this.maxMonths || 360))) {
      e['lumpSumMonth'] = `Month must be between 1 and ${this.maxMonths || 360}.`;
    }

    this.formErrors = e;
    return Object.keys(e).length === 0;
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validate();
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.clamp(field);
    this.validate();
  }

  private clamp(field: string): void {
    switch (field) {
      case 'outstandingAmount': if (this.outstandingAmount < 0) this.outstandingAmount = 0; break;
      case 'annualInterestRate':
        if (this.annualInterestRate < 0) this.annualInterestRate = 0;
        if (this.annualInterestRate > this.C.rate.max) this.annualInterestRate = this.C.rate.max;
        break;
      case 'remainingTermYears':
        if (this.remainingTermYears < 0) this.remainingTermYears = 0;
        if (this.remainingTermYears > this.C.term.max) this.remainingTermYears = this.C.term.max;
        this.remainingTermYears = Math.floor(this.remainingTermYears);
        break;
      case 'lumpSumAmount':  if (this.lumpSumAmount < 0)  this.lumpSumAmount = 0;  break;
      case 'lumpSumMonth':   if (this.lumpSumMonth < 1)   this.lumpSumMonth = 1;   this.lumpSumMonth = Math.floor(this.lumpSumMonth); break;
      case 'monthlyExtra':   if (this.monthlyExtra < 0)   this.monthlyExtra = 0;   break;
    }
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  // ── Build prepayment list ─────────────────────────────────────────────────

  private buildPrepayments(): Prepayment[] {
    const list: Prepayment[] = [];
    let id = 1;
    const months = this.maxMonths;

    if (this.monthlyExtra > 0) {
      for (let m = 1; m <= months; m++) {
        list.push({ id: id++, month: m, amount: Math.round(this.monthlyExtra) });
      }
    }
    if (this.lumpSumAmount > 0 && this.lumpSumMonth >= 1) {
      const existing = list.find(p => p.month === Math.floor(this.lumpSumMonth));
      if (existing) existing.amount += Math.round(this.lumpSumAmount);
      else list.push({ id, month: Math.floor(this.lumpSumMonth), amount: Math.round(this.lumpSumAmount) });
    }
    return list;
  }

  // ── Calculate ─────────────────────────────────────────────────────────────

  calculate(): void {
    this.showFormErrors = true;
    this.touched = {
      outstandingAmount: true, annualInterestRate: true,
      remainingTermYears: true, lumpSumMonth: true
    };
    if (!this.validate()) return;

    this.calculating = true;
    this.calcError = false;
    this.cdr.detectChanges();

    try {
      const startDate = this.currentMonthStr();
      const baseEMI = this.calc.computeEMI(
        this.outstandingAmount, this.annualInterestRate, this.remainingTermYears
      );

      const baseResult: ScheduleResult = this.calc.buildSchedule(
        this.outstandingAmount, this.annualInterestRate, this.remainingTermYears,
        baseEMI, [], this.prepaymentOption, startDate
      );
      this.baseSummary = this.calc.buildSummary(baseEMI, baseResult);
      this.baseYearly  = this.calc.buildYearly(baseResult.months);

      const prepayments = this.buildPrepayments();
      this.hasPrepayments = prepayments.length > 0;

      const finalResult: ScheduleResult = this.calc.buildSchedule(
        this.outstandingAmount, this.annualInterestRate, this.remainingTermYears,
        baseEMI, prepayments, this.prepaymentOption, startDate
      );
      const finalEMI = this.prepaymentOption === 'reduce_emi' ? finalResult.finalEMI : baseEMI;
      this.finalSummary   = this.calc.buildSummary(finalEMI, finalResult);
      this.yearlySchedule = this.calc.buildYearly(finalResult.months);

      this.calculated  = true;
      this.calculating = false;
      this.cdr.detectChanges();

      setTimeout(() => {
        if (!this.pieChartRef) return;
        this.charts.render(
          this.pieChartRef.nativeElement,
          this.barChartRef.nativeElement,
          this.lineChartRef.nativeElement,
          {
            loanAmount:      this.outstandingAmount,
            finalSummary:    this.finalSummary!,
            yearlySchedule:  this.yearlySchedule,
            hasPrepayments:  this.hasPrepayments,
            monthlySchedule: finalResult.months,
            baseMonths:      this.baseSummary!.totalMonths,
            baseYearly:      this.baseYearly
          }
        );
      }, 80);
    } catch (err) {
      if (!environment.production) {
        console.error('Prepayment calculation error', err);
      }
      this.calculating = false;
      this.calcError = true;
    }
  }

  reset(): void {
    this.outstandingAmount  = 5_000_000;
    this.annualInterestRate = 8.5;
    this.remainingTermYears = 20;
    this.prepaymentOption   = 'reduce_tenure';
    this.lumpSumAmount      = 500_000;
    this.lumpSumMonth       = 12;
    this.monthlyExtra       = 0;
    this.formErrors  = {};
    this.touched     = {};
    this.showFormErrors = false;
    this.calculated  = false;
    this.calculating = false;
    this.calcError   = false;
    this.baseSummary = null;
    this.finalSummary = null;
    this.yearlySchedule = [];
    this.baseYearly = [];
    this.hasPrepayments = false;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
